import { EditorModus } from '../common/components/EditDialog/EditDialog';
import { Dispatch, SetStateAction, useCallback, useEffect, useState, } from 'react';
import { KeysArray } from '../../models/Constants/RequiredFields';
import { TEntity } from '../../models/entity';
import { isValidDate } from './dateUtils';
import { useTranslation } from 'react-i18next';


export type ErrorObject = { [key: string]: string };

export interface ValidationProps<T extends TEntity> {
    requiredFields?: KeysArray<T>;
    mode: EditorModus;
    initialValidateError?: ErrorObject;
    setValid: Dispatch<SetStateAction<boolean>>;
    setPristine: (value: SetStateAction<boolean>) => void;
}
export const checkIsEmptyField = (fieldValue: any): boolean => {
    const isEmpty =
        fieldValue == null
        || (typeof fieldValue === 'string' && fieldValue.length === 0)
        || (Array.isArray(fieldValue) && fieldValue.length === 0)
        || (typeof fieldValue === 'object' && Object.keys(fieldValue).length === 0 && (fieldValue instanceof Date === false));
    return isEmpty;
};
export function useValidation<T extends TEntity>(
    { requiredFields, mode, setValid, initialValidateError, setPristine }: ValidationProps<T>,
    getErrors?: (
        fieldValues: Partial<T>,
        mode: EditorModus,
        fieldName?: string,
    ) => ErrorObject
) {
    const [errors, setErrors] = useState({} as ErrorObject);

    const { t } = useTranslation();
    useEffect(() => {
        setErrors(initialValidateError ?? {});
        setPristine(false);
    }, [initialValidateError, setPristine]);

    const checkIsValid = useCallback((errors: ErrorObject) => {
        const valid = Object.values({ ...errors }).every(x => !x);
        return valid;
    }, []);

    const handleFieldError = useCallback((prevErrors: ErrorObject, fieldName: string, isValid: boolean, errorMessage?: string): ErrorObject => {
        if (isValid && prevErrors[fieldName]) {
            let newValue = { ...prevErrors };
            delete newValue[fieldName];
            return newValue;
        }
        if (!isValid && !prevErrors.fieldName) {
            return {
                ...prevErrors,
                [fieldName]: errorMessage ?? 'validation error',
            };
        }
        return prevErrors;
    }, []);

    const handleFieldValidation = useCallback((fieldName: string, isValid: boolean, errorMessage?: string) => {
        setErrors(prev => {
            const err = handleFieldError(prev, fieldName, isValid, errorMessage);
            return err;
        });
    }, [handleFieldError]);


    //check that field is a valid or not and add a message 
    const validateField = useCallback((fieldValue: any, key: string, setValidation: boolean = false, informUser: boolean = true, extraErrors: ErrorObject = {}) => {
        if (mode === EditorModus.Soek) {
            setErrors({} as ErrorObject);
            return;
        }
        let isValid = true;
        let temp: ErrorObject = {};
        if (requiredFields) {
            requiredFields.forEach(key => {
                if (typeof (key) === 'string') {
                    const isError = checkIsEmptyField(fieldValue);
                    if (isError) {
                        temp = { ...temp, [key as string]: `${key.toString()} må være satt` };
                    }
                }
                if (typeof (key) === 'object') {
                    const isError = checkIsEmptyField(fieldValue);
                    if (isError) {
                        temp = { ...temp, [key.key as string]: `${key.title.toString()} må være satt` };
                    }
                }
            });
        }
        if (fieldValue instanceof Date && !isValidDate(fieldValue)) {
            temp = { ...temp, [key as string]: `${key.toString()} - feil dato` };
        }
        const formErr = getErrors?.({ [key]: fieldValue } as T, mode, key) ?? ({} as ErrorObject);
        if (formErr[key]) temp = { [key]: formErr[key] };
        if (extraErrors[key]) temp = { [key]: extraErrors[key] };
        isValid = checkIsValid({ [key]: temp[key] });
        setErrors(prev => {
            isValid = checkIsValid({ ...prev, ...extraErrors, [key]: temp[key] });
            if (setValidation) setValid(isValid);
            if (temp[key] !== prev[key] && informUser) {
                return { ...prev, [key]: temp[key] };
            }
            return prev;
        });

        return isValid;
    },
        [getErrors, mode, requiredFields, checkIsValid, setValid]
    );


    //fieldValues - is the value of the whole object, errorPropertyNames is an array that contains properties that we use to add an error message
    //if errorPropertyNames is empty - ([]) then we don`t add a message, by default we add a message for all properties of the object
    const validate = useCallback((fieldValues: T, errorPropertyNames: KeysArray<T> = Object.keys(fieldValues) as KeysArray<T>, extraErrors: ErrorObject = {}): boolean => {
        if (mode === EditorModus.Soek) {
            setErrors({} as ErrorObject);
            return true;
        }
        let temp: ErrorObject = {};

        if (requiredFields) {
            requiredFields.forEach(key => {
                if (typeof (key) === 'string') {
                    const val = fieldValues[key];
                    const isError = checkIsEmptyField(val);
                    if (isError) {
                        temp = { ...temp, [key]: `${t(key.toString())} må være satt` };
                    }
                }
                if (typeof (key) === 'object') {
                    const val = fieldValues[key.key];
                    const isError = checkIsEmptyField(val);
                    if (isError) {
                        temp = { ...temp, [key.key]: `${t(key.title.toString())} må være satt` };
                    }
                }
            });
        }

        const formErr = getErrors?.(fieldValues, mode) ?? ({} as ErrorObject);
        const errors: ErrorObject = { ...temp, ...formErr };
        setErrors(errors);
        const isValid = checkIsValid({ ...errors, ...extraErrors });
        setValid(isValid);
        return isValid;
    }, [getErrors, mode, requiredFields, t, checkIsValid, setValid]);
    return { handleFieldValidation, validate, validateField, errors, setErrors };
}
